import {
  registerControl,
} from "./controlCore.js";

let nextDateControlId = 1;

const WRAPPER_CLASSES = "space-y-1";
const INPUT_SHELL_CLASSES = "relative";
const INPUT_CLASSES = [
  "dfc-date-control-input h-10 w-full rounded-md border border-gray-200 bg-white py-1 pl-2 pr-9 text-base text-gray-700 sm:h-[30px] sm:text-sm",
  "transition-colors hover:border-[var(--dfc-brand-primary)] focus:hover:border-gray-200 focus:outline-none",
  "disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-400",
].join(" ");
const TRAILING_BUTTON_CLASSES = [
  "absolute inset-y-0 right-1 my-auto hidden h-7 w-7 sm:inline-flex",
  "items-center justify-center rounded text-gray-500",
  "transition-colors hover:text-gray-700 focus:outline-none",
].join(" ");
const LABEL_CLASSES = "block text-base text-gray-500 sm:text-sm";
const RANGE_CLASSES = "grid grid-cols-1 gap-3 sm:grid-cols-2";
const FOCUS_BORDER_COLOR = "#e5e7eb";
const FOCUS_SHADOW = "0 0 0 1.5px rgba(71, 85, 105, 0.55)";
const INVALID_SHADOW = "0 0 0 1.5px rgba(220, 38, 38, 0.22)";

function normalizeDateValue(value, range = false) {
  if (range) {
    return {
      start: value?.start || "",
      end: value?.end || "",
    };
  }
  return value === null || value === undefined ? "" : String(value);
}

function getMirrorDateValue(value) {
  if (value && typeof value === "object") {
    return JSON.stringify(value);
  }
  return String(value ?? "");
}

function getRangeInputType(config) {
  return config.rangeInputType || "date";
}

function setInputAttributes(input, config) {
  if (config.min) {input.min = config.min;}
  if (config.max) {input.max = config.max;}
  if (config.step) {input.step = config.step;}
  input.disabled = Boolean(config.disabled);
  input.readOnly = Boolean(config.readOnly);
  input.required = Boolean(config.required);
}

function isControlInvalid(state) {
  return Boolean(state.root?.hasAttribute("data-control-invalid"));
}

function applyInvalidInputStyle(input, state) {
  if (!isControlInvalid(state)) {return;}
  input.style.borderColor = "#dc2626";
  input.style.boxShadow = INVALID_SHADOW;
}

function applyDateTextColor(input) {
  input.style.color = input.value ? "#374151" : "#9ca3af";
}

function getBrandPrimaryColor(state) {
  const brandOwner = state.root.closest?.("#sandbox-view") || state.root;
  return getComputedStyle(brandOwner).getPropertyValue("--dfc-brand-primary").trim() || "#ffd612";
}

function bindDateHoverBorder(input, state) {
  const apply = () => {
    if (!isControlInvalid(state) && !input.disabled && document.activeElement !== input) {
      input.style.borderColor = getBrandPrimaryColor(state);
    }
  };
  const reset = () => {
    if (!isControlInvalid(state) && document.activeElement !== input) {
      input.style.borderColor = "";
    }
  };
  input.addEventListener("pointerenter", apply);
  input.addEventListener("mouseover", apply);
  input.addEventListener("mouseenter", apply);
  input.addEventListener("pointerleave", reset);
  input.addEventListener("mouseleave", reset);
}

function bindDateFocusShadow(input, state) {
  input.addEventListener("focus", () => {
    input.style.borderColor = isControlInvalid(state) ? "#dc2626" : FOCUS_BORDER_COLOR;
    input.style.boxShadow = isControlInvalid(state) ? INVALID_SHADOW : FOCUS_SHADOW;
  });
  input.addEventListener("blur", () => {
    input.style.borderColor = isControlInvalid(state) ? "#dc2626" : "";
    input.style.boxShadow = isControlInvalid(state) ? INVALID_SHADOW : "";
    applyDateTextColor(input);
  });
}

function createLabel(text, inputId) {
  if (!text) {return null;}

  const label = document.createElement("label");
  label.className = LABEL_CLASSES;
  label.htmlFor = inputId;
  label.textContent = text;
  return label;
}

function openNativeDatePicker(input) {
  input.focus({preventScroll: true});
  if (typeof input.showPicker === "function") {
    try {
      input.showPicker();
      return;
    } catch {
      // Fall through to focus-only behavior for browsers that reject showPicker.
    }
  }
}

function createTrailingButton(state, input) {
  const button = document.createElement("button");
  button.type = "button";
  button.className = TRAILING_BUTTON_CLASSES;
  button.disabled = state.disabled || state.readOnly;
  button.tabIndex = -1;
  button.setAttribute("aria-label", "Open date picker");
  button.onclick = () => openNativeDatePicker(input);

  const icon = document.createElement("span");
  icon.className = "material-symbols-outlined text-[20px] leading-none";
  if (state.disabled) {
    button.classList.remove("text-gray-500", "hover:text-gray-700");
    button.classList.add("text-gray-400");
  }
  icon.setAttribute("aria-hidden", "true");
  icon.textContent = "expand_more";
  button.append(icon);
  return button;
}

function createInput(state, value, inputName, labelText) {
  const field = document.createElement("div");
  field.className = WRAPPER_CLASSES;

  const inputId = `${state.id}-${inputName}`;
  const label = createLabel(labelText, inputId);
  if (label) {
    field.append(label);
  }

  const input = document.createElement("input");
  input.id = inputId;
  input.type = state.inputType;
  if (!state.range && state.nativeInputName) {
    input.name = state.nativeInputName;
  }
  input.value = value || "";
  input.className = INPUT_CLASSES;
  input.autocomplete = "off";
  input.dataset.nativeFieldSkip = "true";
  setInputAttributes(input, state);
  applyDateTextColor(input);
  applyInvalidInputStyle(input, state);
  bindDateHoverBorder(input, state);
  bindDateFocusShadow(input, state);
  input.addEventListener("input", () => {
    applyDateTextColor(input);
  });
  input.addEventListener("change", () => {
    updateValueFromNativeInput(state, inputName, input.value);
  });

  const inputShell = document.createElement("div");
  inputShell.className = INPUT_SHELL_CLASSES;
  inputShell.append(input, createTrailingButton(state, input));
  field.append(inputShell);
  return field;
}

function updateValueFromNativeInput(state, inputName, value) {
  if (state.range) {
    state.value = {
      ...state.value,
      [inputName]: value,
    };
    state.handle.setValue(state.value);
    return;
  }

  state.value = value;
  state.handle.setValue(state.value);
}

function validateDateControl(state, value) {
  if (state.range) {
    if (state.required && (!value.start || !value.end)) {
      return {valid: false, message: state.requiredMessage};
    }
    if (value.start && value.end && value.start > value.end) {
      return {valid: false, message: "Start date must be before end date."};
    }
    return {valid: true, message: ""};
  }

  if (state.required && !value) {
    return {valid: false, message: state.requiredMessage};
  }

  return {valid: true, message: ""};
}

function renderDateControl(state) {
  state.root.replaceChildren();
  state.root.className = [state.baseClassName, WRAPPER_CLASSES].filter(Boolean).join(" ");
  state.root.toggleAttribute("data-date-disabled", state.disabled);

  if (state.range) {
    const range = document.createElement("div");
    range.className = RANGE_CLASSES;
    range.append(createInput(state, state.value.start, "start", state.startLabel));
    range.append(createInput(state, state.value.end, "end", state.endLabel));
    state.root.append(range);
  } else {
    state.root.append(createInput(state, state.value, "value", state.label));
  }

  if (state.validationMessageElement) {
    state.root.append(state.validationMessageElement);
  }
}

/**
 * Mounts a native-backed date, datetime-local, or date-range control.
 * @param {object} config Date control configuration.
 * @param {HTMLElement} config.root Root element to own the rendered control.
 * @param {string} [config.name] Field name used for registered value extraction.
 * @param {string} [config.nativeInputName] Optional name for the visible native input.
 * @param {"date"|"datetime-local"|"range"} [config.type="date"] Date control type.
 * @param {string|object} [config.value] Initial date string or range object.
 * @param {boolean} [config.required=false] Require a value.
 * @param {boolean} [config.disabled=false] Disable interaction and extraction.
 * @param {boolean} [config.readOnly=false] Make native inputs read-only.
 * @param {string} [config.min] Native minimum date value.
 * @param {string} [config.max] Native maximum date value.
 * @param {string} [config.step] Native step value.
 * @param {Function} [config.onChange] Change callback receiving canonical value.
 * @returns {object} Date control lifecycle handle.
 */
export function createDateControl(config) {
  const isRange = config.type === "range";
  const state = {
    id: config.id || `date-control-${nextDateControlId}`,
    baseClassName: config.root.className,
    root: config.root,
    name: config.name || "",
    nativeInputName: config.nativeInputName || "",
    range: isRange,
    inputType: isRange ? getRangeInputType(config) : config.type || "date",
    value: normalizeDateValue(config.value, isRange),
    required: Boolean(config.required),
    disabled: Boolean(config.disabled),
    readOnly: Boolean(config.readOnly),
    min: config.min || "",
    max: config.max || "",
    step: config.step || "",
    label: config.label || "",
    startLabel: config.startLabel || "Start",
    endLabel: config.endLabel || "End",
    requiredMessage: config.requiredMessage || "This field is required.",
    validationMessageElement: document.createElement("p"),
    validationObserver: null,
    handle: null,
  };
  nextDateControlId += 1;

  state.validationMessageElement.className = "mt-1 hidden text-xs font-medium text-red-600";

  const controlHandle = registerControl({
    root: state.root,
    name: state.name,
    value: state.value,
    required: state.required,
    disabled: state.disabled,
    mirror: config.mirror,
    mirrorInput: config.mirrorInput,
    mirrorFormatter: getMirrorDateValue,
    validationMessageElement: state.validationMessageElement,
    getValue: () => state.value,
    setValue: (value) => {
      state.value = normalizeDateValue(value, state.range);
      renderDateControl(state);
      if (typeof config.onChange === "function") {
        config.onChange(state.value);
      }
    },
    validate: (value) => validateDateControl(state, value),
    mount: () => {
      renderDateControl(state);
      state.validationObserver = new MutationObserver((mutations) => {
        if (mutations.some((mutation) => mutation.attributeName === "data-control-invalid")) {
          renderDateControl(state);
        }
      });
      state.validationObserver.observe(state.root, {attributes: true});
    },
    update: (updates) => {
      if ("disabled" in updates) {state.disabled = Boolean(updates.disabled);}
      if ("readOnly" in updates) {state.readOnly = Boolean(updates.readOnly);}
      if ("required" in updates) {state.required = Boolean(updates.required);}
      if ("min" in updates) {state.min = updates.min || "";}
      if ("max" in updates) {state.max = updates.max || "";}
      if ("value" in updates) {state.value = normalizeDateValue(updates.value, state.range);}
      renderDateControl(state);
    },
    destroy: () => {
      state.validationObserver?.disconnect();
      state.validationObserver = null;
      state.root.replaceChildren();
    },
  });

  state.handle = controlHandle;
  return controlHandle;
}
